import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  Cog,
  Trash2,
  SquarePen,
  RotateCcw,
  CalendarClock,
  CircleSlash,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatDateTime, formatCreatedAtListing } from "../../utils/formatters";
import { CLIENT_DELETION_GRACE_DAYS } from "../../config/clientUi.constants";

import Avatar from "@mui/material/Avatar";

const HEADERS = [
  {
    header: "Admin User",
    key: "admin_user",
  },
  {
    header: "Created By",
    key: "created_by",
  },
  {
    header: "Updated By",
    key: "updated_by",
  },
  {
    header: "Updated At",
    key: "updated_at",
  },
];

const AllClients = ({
  clients = [],
  handleDeactivateClient,
  handleActivateClient,
  handleEditClient,
  handlePermanentDeleteClient,
  handleViewClientConfiguration,
  handleViewClientProjects,
  activateLoading = false,
  activatingClientId = null,
}) => {
  const { user } = useAuth();

  const getInitials = (name = "") => {
    const words = name.trim().split(" ").filter(Boolean);

    if (words.length === 0) return "?";
    if (words.length === 1) return words[0][0].toUpperCase();

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const isUserChiefAdmin = () => {
    const userRole = user?.role_name || user?.role;
    if (!userRole) return false;
    const normalizedRole = userRole.toLowerCase().replace(/[_\s-]/g, "-");
    return normalizedRole === "chief-admin";
  };

  const formatCellValue = (value, key) => {
    if (value == null || value === "") return "-";
    if (typeof value === "object") {
      return value.full_name ?? value.name ?? "-";
    }
    if (
      key === "updated_at" &&
      (typeof value === "string" || value instanceof Date)
    ) {
      return formatDateTime(value);
    }
    return String(value);
  };

  return (
    <Stack gap={"8px"} sx={{ width: "100%", minWidth: 0 }}>
      {clients?.map((client) => (
        <Stack
          key={client.client_id}
          sx={{
            background: "#FAF9F9FF",
            gap: { xs: 1.5, sm: "4px" },
            boxShadow: " 0px 4px 9px #1d191a1C, 0px 0px 2px #1d191a1F",
            padding: { xs: 1.5, sm: "6px" },
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <Avatar
            src={client?.logo_url || ""}
            alt={client?.client_name}
            imgProps={{ style: { objectFit: "cover" } }}
            sx={{
              width: { xs: 120, sm: 150 },
              height: { xs: 80, sm: 100 },
              alignSelf: { xs: "center", sm: "flex-start" },
              flexShrink: 0,
              borderRadius: "12px",
              fontSize: "22px",
              fontWeight: 800,
              background: "#EDEBFF",
              color: "#3B3B3B",
              border: "1px solid #E6E6E6",
            }}
          >
            {getInitials(client?.client_name)}
          </Avatar>

          <Stack
            sx={{
              ml: { xs: 0, sm: "8px" },
              mt: { xs: 0, sm: 0 },
              gap: "4px",
              width: "100%",
              minWidth: 0,
            }}
          >
            <Stack
              sx={{
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", md: "flex-start" },
                gap: { xs: 1.5, md: 1 },
              }}
            >
              <Stack
                flexDirection={"row"}
                alignItems={"center"}
                flexWrap="wrap"
                gap={1}
                sx={{ minWidth: 0, flex: { md: "1 1 auto" } }}
              >
                <Typography
                  onClick={() => handleViewClientProjects(client)}
                  sx={{
                    fontSize: "18px",
                    cursor: "pointer",
                    fontWeight: 700,
                    maxWidth: { xs: "100%", sm: "300px" },
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {client.client_name}
                </Typography>
                <Stack flexDirection={"row"} flexWrap="wrap" sx={{ columnGap: 0.5, rowGap: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      whiteSpace: "pre",
                    }}
                  >
                    Created At:{" "}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {formatCreatedAtListing(client.created_at)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack
                flexDirection={"row"}
                gap={"10px"}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  width: { xs: "100%", md: "auto" },
                }}
              >
                {client.deletion_days_remaining != null && !client.is_active && (
                  <Stack
                    flexDirection={"row"}
                    sx={{
                      minHeight: "32px",
                      padding: "4px 8px",
                      color: "#DE3B40FF",
                      fontSize: "14px",
                      fontWeight: 500,
                      maxWidth: { xs: "100%", sm: 420 },
                      alignItems: "flex-start",
                    }}
                    title={`Permanent removal after ${CLIENT_DELETION_GRACE_DAYS} days inactive`}
                  >
                    <CalendarClock
                      className="w-4 h-4"
                      style={{ marginRight: "4px", flexShrink: 0 }}
                    />
                    <span>
                      {client.deletion_days_remaining === 0
                        ? "Scheduled for removal (grace period ended)"
                        : `${client.deletion_days_remaining} day${
                            client.deletion_days_remaining === 1 ? "" : "s"
                          } until permanent removal`}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          opacity: 0.85,
                          ml: 0.5,
                        }}
                      >
                        ({CLIENT_DELETION_GRACE_DAYS}-day policy)
                      </Typography>
                    </span>
                  </Stack>
                )}
                {client.is_active ? (
                  <Stack
                    onClick={() => handleDeactivateClient(client)}
                    sx={{
                      height: "32px",
                      cursor: "pointer",
                      padding: "4px 8px",
                      alignItems: "center",
                      background: "#FDF1F5FF",
                      color: "#E8618CFF",
                      flexDirection: "row",
                      gap: "4px",
                      borderRadius: "6px",
                    }}
                  >
                    <CircleSlash className="w-4 h-4" />
                    Inactive
                  </Stack>
                ) : (
                  <Stack
                    onClick={() => handleActivateClient?.(client)}
                    sx={{
                      height: "32px",
                      cursor: "pointer",
                      padding: "4px 8px",
                      alignItems: "center",
                      background: "#E8F5E9",
                      color: "#2E7D32",
                      flexDirection: "row",
                      gap: "4px",
                      borderRadius: "6px",
                    }}
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reactivate
                  </Stack>
                )}
                {client.is_active && (
                  <Stack
                    onClick={() => handleEditClient(client)}
                    sx={{
                      height: "32px",
                      cursor: "pointer",
                      padding: "4px 8px",
                      alignItems: "center",
                      background: "#F2F2FDFF",
                      color: "#636AE8FF",
                      flexDirection: "row",
                      gap: "4px",
                      borderRadius: "6px",
                    }}
                  >
                    <SquarePen className="w-4 h-4" />
                    Edit
                  </Stack>
                )}
                <Stack
                  onClick={() => handlePermanentDeleteClient(client)}
                  sx={{
                    height: "32px",
                    cursor: "pointer",
                    padding: "4px 8px",
                    alignItems: "center",
                    background: "#FDF2F2FF",
                    color: "#DE3B40FF",
                    flexDirection: "row",
                    gap: "4px",
                    borderRadius: "6px",
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Stack>
                {isUserChiefAdmin() && (
                  <Stack
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleViewClientConfiguration?.(client);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleViewClientConfiguration?.(client);
                      }
                    }}
                    sx={{
                      height: "32px",
                      cursor: "pointer",
                      padding: "4px 8px",
                      alignItems: "center",
                      background: "#F1F8FDFF",
                      color: "#379AE6FF",
                      flexDirection: "row",
                      gap: "4px",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  >
                    <Cog className="w-4 h-4" />
                    System Configuration
                  </Stack>
                )}
              </Stack>
            </Stack>

            <Stack
              style={{
                background: client.is_active ? "#eefdf3" : "#F5C4C6FF",
                width: "max-content",
                padding: "0px 10px",
                borderRadius: "20px",
              }}
            >
              <Typography
                sx={{ color: client.is_active ? "#117B34FF" : "#DE3B40FF" }}
              >
                {client.is_active ? "Active" : "Inactive"}
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1,
                width: "100%",
                minWidth: 0,
              }}
            >
              {HEADERS?.map((header) => (
                <Stack
                  key={header.key}
                  sx={{
                    background: "#EEEBECFF",
                    borderRadius: "6px",
                    minHeight: "46px",
                    minWidth: 0,
                    py: 0.75,
                    pr: 1.25,
                    pl: "20px",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                    {header.header}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {formatCellValue(client[header.key], header.key)}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

export default AllClients;
